import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { listWithFilters } from '../api';
import { sumHoras } from '../utils';
import type { TimesheetEntryListItem } from '../types';

interface ResumoHorasDialogProps {
  open: boolean;
  onClose: () => void;
  /** Colaborador cujos lançamentos serão resumidos. Admin passa o selecionado; colaborador o próprio. */
  colaboradorCodigo: number | null;
  /**
   * Data (ISO `YYYY-MM-DD`) cujo mês o resumo abre selecionado — o período já
   * escolhido na tela de baixo. A cada abertura o calendário volta pra esse mês;
   * navegar dentro do resumo continua livre. Omitido/`null` = mês atual.
   */
  initialDate?: string | null;
}

interface DayCell {
  date: dayjs.Dayjs;
  iso: string;
  inMonth: boolean;
  isToday: boolean;
}

/** Monta a grade de 6 semanas (Domingo→Sábado) que cobre o mês de `ref`. */
function buildCalendar(ref: dayjs.Dayjs): DayCell[] {
  const start = ref.startOf('month').startOf('week'); // week começa no domingo (locale pt-br)
  const today = dayjs().format('YYYY-MM-DD');
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const date = start.add(i, 'day');
    const iso = date.format('YYYY-MM-DD');
    cells.push({
      date,
      iso,
      inMonth: date.month() === ref.month(),
      isToday: iso === today,
    });
  }
  return cells;
}

export function ResumoHorasDialog({
  open,
  onClose,
  colaboradorCodigo,
  initialDate,
}: ResumoHorasDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [ref, setRef] = useState<dayjs.Dayjs>(() =>
    (initialDate ? dayjs(initialDate) : dayjs()).startOf('month'),
  );

  // Ao abrir, alinha o calendário ao período da tela de baixo (não reabre no
  // último mês navegado). Dentro do resumo, a navegação de mês segue livre.
  // Padrão React de ajuste de estado durante o render (não em efeito): detecta
  // a transição fechado→aberto comparando com o valor anterior.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setRef((initialDate ? dayjs(initialDate) : dayjs()).startOf('month'));
  }

  const monthStart = ref.startOf('month');
  const dataInicial = monthStart.format('YYYY-MM-DD');
  const dataFinal = ref.endOf('month').format('YYYY-MM-DD');

  const listQuery = useQuery({
    // pageSize alto: um mês raramente passa de 500 lançamentos de um único colaborador.
    queryKey: ['timesheet-entries', 'resumo', colaboradorCodigo, dataInicial, dataFinal],
    queryFn: () =>
      listWithFilters(
        { page: 1, pageSize: 500, sortBy: 'data', sortOrder: 'asc' },
        { colaboradorCodigo, dataInicial, dataFinal },
      ),
    enabled: open && colaboradorCodigo != null,
    placeholderData: (previous) => previous,
  });

  const rows = useMemo(() => listQuery.data?.data ?? [], [listQuery.data]);

  // Agrega horas + contagem por dia (YYYY-MM-DD).
  const byDay = useMemo(() => {
    const map = new Map<string, TimesheetEntryListItem[]>();
    for (const r of rows) {
      const arr = map.get(r.data);
      if (arr) arr.push(r);
      else map.set(r.data, [r]);
    }
    return map;
  }, [rows]);

  const cells = useMemo(() => buildCalendar(ref), [ref]);

  const monthLabel = useMemo(() => {
    const raw = monthStart.locale('pt-br').format('MMMM [de] YYYY');
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, [monthStart]);

  const weekdays = useMemo(() => {
    // Domingo→Sábado, nomes completos como no GeneXus.
    const base = dayjs().startOf('week');
    return Array.from({ length: 7 }, (_, i) => {
      const name = base.add(i, 'day').locale('pt-br').format('dddd');
      return name.charAt(0).toUpperCase() + name.slice(1);
    });
  }, []);

  const totalMes = useMemo(() => sumHoras(rows), [rows]);

  const colaboradorNome = rows[0]?.colaboradorNome;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="resumo-horas-title"
    >
      <DialogTitle id="resumo-horas-title" sx={{ pr: 6 }}>
        {t('features.apontamentos.resumo.title')}
        {colaboradorNome ? (
          <Typography variant="body2" color="text.secondary" component="div">
            {colaboradorNome}
          </Typography>
        ) : null}
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }} aria-hidden={false}>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }} aria-live="polite">
            {monthLabel}
          </Typography>
          <Button size="small" onClick={() => setRef(dayjs().startOf('month'))}>
            {t('features.apontamentos.resumo.today')}
          </Button>
          <IconButton
            aria-label={t('features.apontamentos.meus.prevMonth')}
            onClick={() => setRef((r) => r.subtract(1, 'month').startOf('month'))}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label={t('features.apontamentos.meus.nextMonth')}
            onClick={() => setRef((r) => r.add(1, 'month').startOf('month'))}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {weekdays.map((w) => (
            <Box
              key={w}
              sx={{
                p: 1,
                textAlign: 'center',
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                {w}
              </Typography>
            </Box>
          ))}

          {cells.map((cell, idx) => {
            const entries = byDay.get(cell.iso);
            const col = idx % 7;
            const row = Math.floor(idx / 7);
            return (
              <Box
                key={cell.iso}
                sx={{
                  minHeight: { xs: 64, sm: 88 },
                  p: 0.75,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: (theme) => (col < 6 ? `1px solid ${theme.palette.divider}` : 'none'),
                  borderBottom: (theme) =>
                    row < 5 ? `1px solid ${theme.palette.divider}` : 'none',
                  bgcolor: cell.isToday
                    ? 'warning.light'
                    : cell.inMonth
                      ? 'background.paper'
                      : 'action.hover',
                  opacity: cell.inMonth ? 1 : 0.55,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: 'right',
                    color: cell.inMonth ? 'text.primary' : 'text.disabled',
                  }}
                >
                  {cell.date.format('DD')}
                </Typography>
                {entries && entries.length > 0 ? (
                  <Box sx={{ mt: 'auto', textAlign: 'right' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontWeight: 600, lineHeight: 1.2 }}
                    >
                      {sumHoras(entries)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('features.apontamentos.resumo.lancamentos', { count: entries.length })}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('features.apontamentos.resumo.totalMes')}:{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {listQuery.isLoading ? '—' : totalMes}
            </Box>
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
